import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const cases = [
  {
    code: '本科04',
    title: '专题目标展开边界候选',
    desc: '拟检验课程是否较多回答"为什么文化自信"，但对文化强国建设的对象、路径和机制展开不足',
    status: '待补证据',
    tag: '边界案例',
    accent: true,
  },
  {
    code: '本科09',
    title: '整体成熟候选',
    desc: '形式不炫目但专题关系完整，适合展示专题把握稳定、理论关系完整的课程',
    status: '人工两侧头部',
    tag: '正向案例',
    accent: false,
  },
  {
    code: '本科11',
    title: '现实治理连接候选',
    desc: '现实治理材料、青年责任与课程专题之间建立知识关系，材料推动理论解释',
    status: '人工两侧头部',
    tag: '正向案例',
    accent: false,
  },
];

export default function P32TopicAlignment({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);

  return (
    <div className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-background-50">
      <div ref={orb1Ref} className="absolute top-20 right-24 w-80 h-80 rounded-full bg-primary-100/10 blur-[100px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-28 left-12 w-96 h-96 rounded-full bg-accent-100/8 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-10 md:py-12">
        <div ref={titleRef} className="text-center mb-5 md:mb-7">
          <div className="flex items-center justify-center gap-2 mb-3">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-100/60 text-primary-700 text-[11px] font-body font-semibold">
              <i className="ri-scan-line text-xs" /> 技术点五
            </span>
          </div>
          <h2 className="font-heading text-2xl md:text-4xl lg:text-[2.75rem] font-bold text-foreground-950 transition-all duration-1000 delay-100">
            知识图谱与<span className="text-primary-600">专题咬合</span>
          </h2>
        </div>

        {/* 核心主张 */}
        <div className="max-w-3xl mx-auto mb-6 transition-all duration-1000 delay-200">
          <div className="p-4 rounded-2xl glass-liquid-gold border-accent-200/40 text-center">
            <div className="relative z-10">
              <p className="font-heading text-base md:text-lg font-bold text-foreground-800">
                <span className="text-accent-600">"出现概念"</span> ≠ <span className="text-primary-600">"完成专题"</span>
              </p>
              <p className="text-xs text-foreground-500 font-body mt-1">
                系统既要识别教师有没有讲到，也要判断讲到了什么深度、覆盖了哪些关系、是否形成完整的理论闭环
              </p>
            </div>
          </div>
        </div>

        {/* 三列案例 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-5xl mx-auto mb-5 transition-all duration-1000 delay-400">
          {cases.map((c, i) => (
            <div key={c.code} className={`p-4 rounded-2xl transition-all duration-500 hover:scale-[1.02] ${c.accent ? 'glass-liquid-gold border-accent-200/30' : 'glass-liquid border-primary-200/30'}`} style={{ transitionDelay: `${400 + i * 100}ms` }}>
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-heading text-sm font-bold text-foreground-800">{c.code}</h3>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-body font-semibold ${c.accent ? 'bg-accent-100/60 text-accent-700' : 'bg-primary-100/60 text-primary-700'}`}>{c.tag}</span>
                </div>
                <p className="text-xs font-body font-semibold text-foreground-700 mb-1.5">{c.title}</p>
                <p className="text-[11px] text-foreground-500 font-body leading-relaxed mb-2">{c.desc}</p>
                <div className="flex items-center gap-1.5 pt-2 border-t border-background-200/60">
                  <i className="ri-information-line text-foreground-400 text-[10px]" />
                  <span className="text-[10px] text-foreground-400 font-body">{c.status}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* 数据 */}
        <div className="max-w-3xl mx-auto text-center transition-all duration-1000 delay-900">
          <div className="inline-flex flex-wrap items-center justify-center gap-3">
            <div className="px-3 py-2 rounded-xl glass border-white/50 text-xs text-foreground-500 font-body">
              "理论不透"总分差约<span className="font-bold text-primary-700">-3.8</span>
            </div>
            <div className="px-3 py-2 rounded-xl glass border-white/50 text-xs text-foreground-500 font-body">
              "逻辑强"正向差约<span className="font-bold text-primary-700">+2.7</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}